import React, {ReactNode, useEffect, useMemo, useState} from "react";
import {
	Avatar,
	Box,
	Button,
	Card,
	Chip,
	Grid,
	InputAdornment,
	LinearProgress,
	Link,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import {InterviewBoard, InterviewBoardRow, InterviewRestControllerApiFactory} from "arteco-api-client-ts";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import dayjs from "dayjs";
import {useApiManager} from "../../../../components/utils/api";
import {dateToString} from "../../../../components/utils/dates";
import {INTERVIEW_EXIT_RISK_LABELS, INTERVIEW_HEALTH_LABELS} from "../../../../components/form/types";
import {avatarColor, initials, relative, Signal, SoftChip, Tone, toneColor} from "./ui";

const STATUS: Record<string, { label: string, tone: Tone }> = {
	OVERDUE: {label: "Vencida", tone: "error"},
	DUE_SOON: {label: "Toca pronto", tone: "warning"},
	UP_TO_DATE: {label: "Al día", tone: "success"},
	NO_CADENCE: {label: "Sin cadencia", tone: "neutral"},
};

const BASIS: Record<string, string> = {
	PLANNED: "Acordada en la última entrevista",
	FOLLOW_UP: "Seguimiento: la última salió en rojo o con riesgo alto",
	ONBOARDING: "Primeros meses desde el alta",
	REGULAR: "Cadencia normal",
};

const FILTERS = [
	{key: "ALL", label: "Todas"},
	{key: "OVERDUE", label: "Vencidas"},
	{key: "DUE_SOON", label: "Tocan pronto"},
	{key: "UP_TO_DATE", label: "Al día"},
	{key: "NO_CADENCE", label: "Sin cadencia"},
];

/** Autoevaluación mensual (#13823): «Este mes», o la fecha de la última, en rojo si pasa de un mes. */
const SelfEvaluation = ({row}: { row: InterviewBoardRow }) => {
	if (row.selfEvaluationThisMonth) {
		return <SoftChip label="Este mes" tone="success"/>;
	}
	if (!row.lastSelfEvaluationDate) {
		return <Typography variant="body2" color="text.disabled">Nunca</Typography>;
	}
	const old = dayjs(row.lastSelfEvaluationDate).isBefore(dayjs().subtract(1, "month"));
	return <Typography variant="body2" color={old ? "error.main" : undefined}>{dateToString(row.lastSelfEvaluationDate)}</Typography>;
};

const PersonCell = ({row}: { row: InterviewBoardRow }) => (
	<Stack direction="row" spacing={1.5} alignItems="center" sx={{minWidth: 0}}>
		<Avatar sx={{width: 34, height: 34, fontSize: 13, fontWeight: 700, bgcolor: avatarColor(row.personName)}}>
			{initials(row.personName)}
		</Avatar>
		<Box sx={{minWidth: 0}}>
			<Link component={RouterLink} underline="hover" color="text.primary" fontWeight={600} variant="body2"
				  to={"/app/admin/employee/person/" + row.personId} sx={{display: "block"}} noWrap>
				{row.personName}
			</Link>
			{row.hireDate && <Typography variant="caption" color="text.secondary">Alta {dateToString(row.hireDate)}</Typography>}
		</Box>
	</Stack>
);

const NextDue = ({row}: { row: InterviewBoardRow }) => {
	if (!row.nextDueDate) {
		return <Typography variant="body2" color="text.disabled">No se evalúa</Typography>;
	}
	return (
		<Tooltip title={row.nextDueBasis ? BASIS[row.nextDueBasis] : ""} placement="top-start">
			<Box>
				<Typography variant="body2" fontWeight={600} color={row.status === "OVERDUE" ? "error.main" : undefined}>
					{relative(row.nextDueDate)}
				</Typography>
				<Typography variant="caption" color="text.secondary">{dateToString(row.nextDueDate)}</Typography>
			</Box>
		</Tooltip>
	);
};

const LastInterview = ({row}: { row: InterviewBoardRow }) => row.lastInterviewId ? (
	<Box>
		<Link component={RouterLink} underline="hover" variant="body2" fontWeight={600}
			  to={"/app/admin/employee/interview/" + row.lastInterviewId}>
			{dateToString(row.lastInterviewDate)}
		</Link>
		<Typography variant="caption" color="text.secondary" display="block">
			{row.lastInterviewerName ? "con " + row.lastInterviewerName : relative(row.lastInterviewDate)}
		</Typography>
	</Box>
) : <Typography variant="body2" color="text.disabled">Nunca</Typography>;

interface FigureProps {
	icon: ReactNode;
	tone: Tone;
	value?: number;
	label: string;
	hint?: ReactNode;
	footer?: ReactNode;
}

const Figure = ({icon, tone, value, label, hint, footer}: FigureProps) => {
	const theme = useTheme();
	const c = toneColor(theme, tone);
	return (
		<Grid item xs={6} sm={4} md>
			<Box sx={{p: 2, borderRadius: 2.5, border: "1px solid", borderColor: "divider", height: "100%", bgcolor: "background.paper"}}>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Box sx={{
						width: 36, height: 36, borderRadius: 2, display: "grid", placeItems: "center", flexShrink: 0,
						bgcolor: alpha(c, 0.12), color: c,
					}}>{icon}</Box>
					{value === undefined
						? <Skeleton width={40} height={40}/>
						: <Typography variant="h5" fontWeight={700} sx={{fontVariantNumeric: "tabular-nums"}}>{value}</Typography>}
				</Stack>
				<Typography variant="body2" color="text.secondary" sx={{mt: 1}}>{label}</Typography>
				{hint}
				{footer}
			</Box>
		</Grid>
	);
};

/** En móvil, una tarjeta por persona en lugar de la tabla de nueve columnas. */
const PersonCard = ({row, onNew}: { row: InterviewBoardRow, onNew: () => void }) => {
	const st = STATUS[row.status || "NO_CADENCE"];
	const Item = ({label, children}: { label: string, children: ReactNode }) => (
		<Box>
			<Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
			{children}
		</Box>
	);
	return (
		<Box sx={{p: 2, borderRadius: 2.5, border: "1px solid", borderColor: "divider", opacity: row.status === "NO_CADENCE" ? 0.7 : 1}}>
			<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
				<PersonCell row={row}/>
				<SoftChip label={st.label} tone={st.tone}/>
			</Stack>
			<Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: 2}}>
				<Item label="Próxima"><NextDue row={row}/></Item>
				<Item label="Última"><LastInterview row={row}/></Item>
				<Item label="Salud · riesgo">
					<Stack direction="row" spacing={1.5}>
						<Signal value={row.lastHealth} labels={INTERVIEW_HEALTH_LABELS}/>
						{row.lastExitRisk && <Signal value={row.lastExitRisk} labels={INTERVIEW_EXIT_RISK_LABELS}/>}
					</Stack>
				</Item>
				<Item label="Autoevaluación"><SelfEvaluation row={row}/></Item>
			</Box>
			<Button fullWidth size="small" variant="outlined" startIcon={<AddRoundedIcon/>} onClick={onNew}
					sx={{mt: 2, textTransform: "none", fontWeight: 600, borderRadius: 2}}>
				Nueva entrevista
			</Button>
		</Box>
	);
};

/**
 * Panel de entrevistas de la plantilla de hoy (#13808): cifras de resumen, filtro por estado y
 * búsqueda, y una fila por persona, vencidas primero. Sustituye a la tarjeta «A quién entrevistar».
 */
export const InterviewBoardPanel = () => {
	const apiManager = useApiManager();
	const navigate = useNavigate();
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const [board, setBoard] = useState<InterviewBoard | undefined>(undefined);
	const [filter, setFilter] = useState("ALL");
	const [term, setTerm] = useState("");

	useEffect(() => {
		apiManager.execute(apiManager.factory(InterviewRestControllerApiFactory).boardInterview())
			.then(resp => setBoard(resp));
	}, []);

	const rows: InterviewBoardRow[] = board?.rows || [];
	const counts = useMemo(() => {
		const c: Record<string, number> = {ALL: rows.length};
		rows.forEach(r => c[r.status || "NO_CADENCE"] = (c[r.status || "NO_CADENCE"] || 0) + 1);
		return c;
	}, [rows]);
	const visible = rows.filter(r => (filter === "ALL" || r.status === filter)
		&& (!term || (r.personName || "").toLowerCase().normalize("NFD").replace(/\p{M}/gu, "")
			.includes(term.toLowerCase().normalize("NFD").replace(/\p{M}/gu, ""))));
	const newInterview = (r: InterviewBoardRow) => navigate("/app/admin/employee/person/" + r.personId + "/interview/new");
	const selfDone = board?.selfEvaluationsThisMonth || 0;
	const selfExpected = board?.selfEvaluationsExpected || 0;

	return (
		<Box>
			<Card elevation={0} sx={{
				width: "100%", borderRadius: 3, border: "1px solid", borderColor: "divider",
				boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)", p: {xs: 2, sm: 3},
			}}>
				<Stack direction={{xs: "column", sm: "row"}} spacing={1} justifyContent="space-between" alignItems={{sm: "flex-start"}}>
					<Box>
						<Typography variant="h6" component="h2" fontWeight={700}>Entrevistas periódicas</Typography>
						{board ? (
							<Typography variant="body2" color="text.secondary" sx={{maxWidth: 760}}>
								Cada {board.cadenceDays} días; cada {board.onboardingDays} durante los {board.onboardingMonths} primeros
								meses desde el alta; seguimiento a los {board.followUpDays} días si la última salió en rojo o con
								riesgo de salida alto. Se ajusta en Configuración › Parámetros (interviews.*).
							</Typography>
						) : <Skeleton width={480}/>}
					</Box>
					<Button size="small" endIcon={<ArrowForwardRoundedIcon/>} onClick={() => navigate("/app/admin/employee/interview")}
							sx={{textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", alignSelf: {xs: "flex-start", sm: "auto"}}}>
						Ver todas las entrevistas
					</Button>
				</Stack>

				<Grid container spacing={1.5} sx={{mt: 1.5, mb: 3}}>
					<Figure icon={<EventBusyRoundedIcon fontSize="small"/>} tone={board?.overdue ? "error" : "neutral"}
							value={board?.overdue} label="Vencidas"/>
					<Figure icon={<EventAvailableRoundedIcon fontSize="small"/>} tone="success" value={board?.upToDate} label="Al día"
							hint={board?.dueSoon ? <Typography variant="caption" color="warning.dark">{board.dueSoon} tocan esta semana</Typography> : undefined}/>
					<Figure icon={<ForumRoundedIcon fontSize="small"/>} tone="info" value={board?.interviewsThisQuarter} label="Este trimestre"/>
					<Figure icon={<FavoriteRoundedIcon fontSize="small"/>} tone={board?.redHealth ? "error" : "neutral"}
							value={board?.redHealth} label="Salud en rojo"/>
					<Figure icon={<FactCheckRoundedIcon fontSize="small"/>} tone="info" value={board ? selfDone : undefined}
							label="Autoevaluaciones del mes"
							hint={board && <Typography variant="caption" color="text.secondary">de {selfExpected} personas</Typography>}
							footer={board && <LinearProgress variant="determinate" value={selfExpected ? selfDone * 100 / selfExpected : 0}
														   sx={{mt: 1, height: 5, borderRadius: 3, "& .MuiLinearProgress-bar": {borderRadius: 3}}}/>}/>
				</Grid>

				<Stack direction={{xs: "column", md: "row"}} spacing={1.5} justifyContent="space-between" alignItems={{md: "center"}} sx={{mb: 2}}>
					<Stack direction="row" spacing={1} sx={{overflowX: "auto", pb: {xs: 0.5, md: 0}}}>
						{FILTERS.map(f => (
							<Chip key={f.key} clickable onClick={() => setFilter(f.key)}
								  label={`${f.label} ${counts[f.key] || 0}`}
								  color={filter === f.key ? "primary" : "default"}
								  variant={filter === f.key ? "filled" : "outlined"}
								  sx={{fontWeight: 600, flexShrink: 0}}/>
						))}
					</Stack>
					<TextField size="small" placeholder="Buscar persona" value={term} onChange={e => setTerm(e.target.value)}
							   sx={{width: {xs: "100%", md: 260}, "& .MuiOutlinedInput-root": {borderRadius: 2}}}
							   InputProps={{startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small"/></InputAdornment>}}/>
				</Stack>

				{!board ? (
					<Stack spacing={1}>{[0, 1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={52}/>)}</Stack>
				) : visible.length === 0 ? (
					<Box sx={{py: 5, textAlign: "center", color: "text.secondary"}}>
						<Typography variant="body2">
							{rows.length === 0 ? "No hay nadie en plantilla." : "Nadie coincide con el filtro."}
						</Typography>
					</Box>
				) : compact ? (
					<Stack spacing={1.5}>
						{visible.map(r => <PersonCard key={r.personId} row={r} onNew={() => newInterview(r)}/>)}
					</Stack>
				) : (
					<TableContainer sx={{border: "1px solid", borderColor: "divider", borderRadius: 2.5}}>
						<Table size="small" sx={{
							tableLayout: "auto",
							"& th": {
								bgcolor: "grey.50", color: "text.secondary", fontSize: 12, fontWeight: 700,
								textTransform: "none", whiteSpace: "nowrap", py: 1.25,
							},
							"& td": {py: 1.25, borderColor: "divider"},
							"& tbody tr:last-of-type td": {borderBottom: 0},
						}}>
							<TableHead>
								<TableRow>
									<TableCell>Persona</TableCell>
									<TableCell>Estado</TableCell>
									<TableCell>Próxima</TableCell>
									<TableCell>Última</TableCell>
									<TableCell align="center">Este año</TableCell>
									<TableCell>Salud</TableCell>
									<TableCell>Riesgo</TableCell>
									<TableCell>Autoevaluación</TableCell>
									<TableCell align="right"/>
								</TableRow>
							</TableHead>
							<TableBody>
								{visible.map(r => {
									const st = STATUS[r.status || "NO_CADENCE"];
									return (
										<TableRow key={r.personId} hover sx={{opacity: r.status === "NO_CADENCE" ? 0.6 : 1}}>
											<TableCell sx={{maxWidth: 280}}><PersonCell row={r}/></TableCell>
											<TableCell><SoftChip label={st.label} tone={st.tone}/></TableCell>
											<TableCell sx={{whiteSpace: "nowrap"}}><NextDue row={r}/></TableCell>
											<TableCell sx={{whiteSpace: "nowrap"}}><LastInterview row={r}/></TableCell>
											<TableCell align="center">
												<Typography variant="body2" color={r.interviewsThisYear ? "text.primary" : "text.disabled"}>
													{r.interviewsThisYear}
												</Typography>
											</TableCell>
											<TableCell><Signal value={r.lastHealth} labels={INTERVIEW_HEALTH_LABELS}/></TableCell>
											<TableCell><Signal value={r.lastExitRisk} labels={INTERVIEW_EXIT_RISK_LABELS}/></TableCell>
											<TableCell sx={{whiteSpace: "nowrap"}}><SelfEvaluation row={r}/></TableCell>
											<TableCell align="right">
												<Button size="small" variant="outlined" startIcon={<AddRoundedIcon/>}
														aria-label={"Nueva entrevista con " + r.personName}
														onClick={() => newInterview(r)}
														sx={{textTransform: "none", fontWeight: 600, borderRadius: 2, whiteSpace: "nowrap"}}>
													Entrevistar
												</Button>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Card>
		</Box>
	);
};
